import { Recycle, CheckCircle, Clock, MapPin, ArrowRight, Trash2, FileCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const VehicleDisposalPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Vehicle Disposal & Scrap Car Collection Hackney | End of Life Vehicle Recycling London"
        description="Professional vehicle disposal, scrap car collection & recycling in Hackney & East London. Unwanted, damaged, salvage vehicles collected. Certificate of Destruction provided. Call 07492 639223."
        keywords="vehicle disposal hackney, scrap car collection, end of life vehicle, car recycling london, vehicle dismantling, salvage car collection, unwanted vehicle removal, certificate of destruction"
      />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-900 via-emerald-800 to-teal-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              ♻️ Responsible Vehicle Disposal
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Vehicle Disposal & Scrap Car Collection in Hackney
            </h1>
            <p className="text-xl text-emerald-100 mb-8">
              Professional end-of-life vehicle disposal, recycling, and scrap car collection across Hackney & East London. We collect unwanted, damaged, salvage, and non-running vehicles. Maximize value from your vehicles with responsible disposal.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-emerald-900 hover:bg-slate-100 text-lg px-8 py-6">
                  <Recycle className="w-5 h-5 mr-2" />
                  Call: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-emerald-900 text-lg px-8 py-6">
                  Get Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-600">Same Day</p>
              <p className="text-sm text-slate-600">Collection Available</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-600">Free</p>
              <p className="text-sm text-slate-600">Vehicle Collection</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-600">Licensed</p>
              <p className="text-sm text-slate-600">Waste Carrier</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-600">CoD</p>
              <p className="text-sm text-slate-600">Provided</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Professional Vehicle Disposal & Recycling in Hackney
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Ovawell Autos provides professional, environmentally responsible vehicle disposal and scrap car collection services across Hackney and East London. Whether your vehicle is unwanted, damaged, salvage, or non-running, we collect it for free and ensure responsible recycling. We're licensed waste carriers and provide Certificate of Destruction (CoD) for complete peace of mind.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Vehicles We Collect for Disposal
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Unwanted vehicles (running or not)",
                "Damaged and accident vehicles",
                "Salvage vehicles",
                "Non-running vehicles",
                "End-of-life vehicles (ELVS)",
                "MOT failures",
                "Fire damaged vehicles",
                "Incomplete vehicles",
                "Vehicles for export",
                "Fleet disposal",
                "Old company vehicles",
                "Any car, van, or truck"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Our Vehicle Disposal Services
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Trash2 className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Free Vehicle Collection</h4>
                      <p className="text-slate-600">
                        We collect your unwanted vehicle for FREE from anywhere in Hackney and East London. Same-day and next-day collection available. No hidden fees - completely free service.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Recycle className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Professional Vehicle Dismantling</h4>
                      <p className="text-slate-600">
                        Vehicles are professionally dismantled into individual parts. Reusable parts are salvaged, fluids are safely removed, and recyclable materials are separated. We maximize value while ensuring environmental compliance.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <FileCheck className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Certificate of Destruction (CoD)</h4>
                      <p className="text-slate-600">
                        We provide official Certificate of Destruction issued by an Authorized Treatment Facility (ATF). This legally confirms your vehicle has been destroyed and removes your liability. Essential for DVLA notification.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Responsible Shell Removal & Recycling</h4>
                      <p className="text-slate-600">
                        After dismantling, vehicle shells are responsibly removed and sent to authorized recycling facilities. We comply with all environmental regulations and ensure proper disposal of hazardous materials.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Export & Resale Services
            </h3>
            <p className="text-slate-700 mb-4">
              For vehicles suitable for resale or export, we can arrange:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-8">
              <li><strong>Delivery & Shipping:</strong> Arrange delivery to buyers or export to international markets</li>
              <li><strong>Trade Sales:</strong> Suitable vehicles can be sold to traders and exporters</li>
              <li><strong>Parts Export:</strong> Individual parts exported to international buyers</li>
              <li><strong>Documentation:</strong> All necessary export paperwork handled professionally</li>
            </ul>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              How Vehicle Disposal Works
            </h3>
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-emerald-600">1</span>
                  </div>
                  <h4 className="font-bold mb-2">Contact Us</h4>
                  <p className="text-sm text-slate-600">Call or quote online - provide vehicle details</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-emerald-600">2</span>
                  </div>
                  <h4 className="font-bold mb-2">Book Collection</h4>
                  <p className="text-sm text-slate-600">Choose convenient date - same day available</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-emerald-600">3</span>
                  </div>
                  <h4 className="font-bold mb-2">Free Collection</h4>
                  <p className="text-sm text-slate-600">We collect your vehicle from your location</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-emerald-600">4</span>
                  </div>
                  <h4 className="font-bold mb-2">Receive CoD</h4>
                  <p className="text-sm text-slate-600">Certificate of Destruction issued within 7 days</p>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Who We Serve
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg text-slate-900 mb-2">Vehicle Owners</h4>
                  <p className="text-sm text-slate-600">Easy disposal of unwanted cars, vans, or trucks. Free collection and all paperwork handled.</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg text-slate-900 mb-2">Traders & Exporters</h4>
                  <p className="text-sm text-slate-600">Efficient vehicle processing for resale or export. Arrange delivery and shipping internationally.</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg text-slate-900 mb-2">Repair Businesses</h4>
                  <p className="text-sm text-slate-600">Disposal of customer vehicles beyond repair. Reliable parts sourcing from dismantled vehicles.</p>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Areas We Cover for Vehicle Collection
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {[
                "Hackney E5, E8, E9",
                "Shoreditch E1, E2",
                "Stratford E15, E20",
                "Bethnal Green E2",
                "Islington N1",
                "Tower Hamlets E1, E3, E14",
                "Bow E3",
                "Mile End E3",
                "Dalston E8",
                "All East London",
                "20-mile radius",
                "Nationwide delivery"
              ].map((area, index) => (
                <div key={index} className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-slate-700">{area}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Frequently Asked Questions
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Is vehicle collection really free?</h4>
                  <p className="text-slate-600">
                    Yes, completely free. We collect your unwanted vehicle at no cost from anywhere in Hackney and East London. No hidden fees whatsoever.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">What is a Certificate of Destruction?</h4>
                  <p className="text-slate-600">
                    A CoD is an official document proving your vehicle has been destroyed at an Authorized Treatment Facility. It protects you from any future liability and must be sent to DVLA.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Do I need to notify DVLA?</h4>
                  <p className="text-slate-600">
                    Yes. Once you receive your Certificate of Destruction, you must inform DVLA that your vehicle has been scrapped. This removes it from their records.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Can you collect non-running vehicles?</h4>
                  <p className="text-slate-600">
                    Absolutely. We collect all vehicles regardless of condition - running, non-running, damaged, incomplete. Our recovery trucks are equipped for all situations.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-emerald-900 to-teal-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Dispose of Your Vehicle?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Free collection across Hackney & East London. Certificate of Destruction provided. Call now to book.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="bg-white text-emerald-900 hover:bg-slate-100 text-lg px-8 py-6">
                <Recycle className="w-5 h-5 mr-2" />
                Call {companyInfo.phone}
              </Button>
            </a>
            <Link to="/#quote">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-emerald-900 text-lg px-8 py-6">
                Get Free Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
